import type { ElementType, HTMLProps, Ref } from 'react';
import { forwardRef } from 'react';
import { colors, fontSizes } from '@anyroad/theme';
import styled from '@emotion/styled';
import { CurrencyInput } from '@anyroad/currency-input';

export type PriceInputProps = HTMLProps<HTMLInputElement> & {
  as?: ElementType<unknown>;
  currencySymbol?: string;
  dataTestId?: string;
  defaultValue?: number | string;
  intlConfig?: {
    currency: string;
    locale: string;
  };
  label?: string;
  onValueChange: (value?: string) => void;
  ref?: Ref<HTMLInputElement>;
  step?: number;
};

const StyledLabel = styled.label({
  display: 'block',
  margin: '0px 0px 10px',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: fontSizes.medium,
  color: colors.text.primary,
  lineHeight: '20px',
  fontFamily: 'soleil',
  fontWeight: '600',
  width: 'initial',
});

const Container = styled.div({
  position: 'relative',
});

export const PriceInput = forwardRef<HTMLInputElement, PriceInputProps>(
  ({ dataTestId, label, onValueChange, ...rest }: PriceInputProps, ref) => {
    const handleValueChange = (value?: string) => {
      // Convert whole numbers to cents by multiplying by 100
      if (value) {
        const numericValue = parseFloat(value.replace(/[^0-9.-]/gu, ''));
        if (!isNaN(numericValue)) {
          const centsValue = Math.round(numericValue * 100).toString();
          onValueChange(centsValue);
          return;
        }
      }
      onValueChange(value);
    };

    return (
      <>
        {label ? <StyledLabel htmlFor={label}>{label}</StyledLabel> : null}
        <Container>
          <CurrencyInput
            data-testid={dataTestId}
            id={label}
            min={0}
            onValueChange={handleValueChange}
            ref={ref}
            {...rest}
          />
        </Container>
      </>
    );
  }
);

PriceInput.displayName = 'PriceInput';
